import {
  createContext,
  useContext,
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  HTMLAttributes
} from 'react';

import dataModel from '../models/data';
import localeModel from '../models/locale';

import {
  MyAccountConfig,
  OnboardingConfig,
  PartnerConfig,
  RawMyAccountConfig,
  RawOnboardingConfig
} from '../interfaces/ClientInfo';
import { useHealthStatus } from './healthStatus';
import { logger } from '@sentry/core';
import { fetchWrapper } from '../lib/fetchWrapper';

export const defaultConfigs: Partial<PartnerConfig> = {
  disableRememberMe: true,
  enableIntercom: false,
  maintenanceMode: false,
  myAccount: {
    oauthConsentTosLink: '',
    oauthConsentPpLink: '',
    oauthConsentNotice: undefined,
    enableMagicLink: false,
    enableOtpLogin: false,
    enableSoloBranding: false,
    authLinks: [],
    extraSignupFields: [],
    enableCustomisation: false,
    authTitle: '',
    authCopy: '',
    serviceName: '',
    customSignupSubmitText: undefined,
    requiredPolicies: []
  },
  onboarding: { backButtonBarText: { en: '', ja: '' } }
};

interface IPartnerConfig {
  config?: PartnerConfig;
  isLoaded: boolean;
  setConfigCountry: Dispatch<SetStateAction<string>>;
}

export const PartnerConfigContext = createContext<IPartnerConfig>({
  config: undefined,
  isLoaded: false,
  setConfigCountry: () => null
});

export function usePartnerConfig() {
  return useContext(PartnerConfigContext);
}

function parsedMyAccountConfig(
  config: RawMyAccountConfig | null
): MyAccountConfig | Record<string, never> {
  if (!config) return {};

  let authLinks = [];

  try {
    authLinks = JSON.parse(config.auth_links);
  } catch (e) {
    logger.error('Failed to parse auth_links', e);
  }

  return {
    authLinks,
    extraSignupFields: config.extra_signup_fields,
    enableCustomisation: config.enable_customisation,
    authTitle: config.auth_title,
    authCopy: config.auth_copy,
    oauthConsentTosLink: config.oauth_consent_tos_link,
    oauthConsentPpLink: config.oauth_consent_pp_link,
    oauthConsentNotice: config.oauth_consent_notice,
    serviceName: config.service_name,
    enableMagicLink: config.enable_magic_link,
    enableOtpLogin: config.enable_otp_login,
    enableSoloBranding: config.enable_solo_branding,
    customSignupSubmitText: config.custom_signup_submit_text,
    requiredPolicies: config.required_policies
  };
}

function parsedOnboardingConfig(
  config: RawOnboardingConfig | null
): OnboardingConfig | null {
  if (!config) return null;

  return {
    backButtonBarText: config.back_button_bar_text
  };
}

export default function PartnerConfigProvider(
  props: HTMLAttributes<HTMLElement>
) {
  const { children } = props;

  const { setStatus } = useHealthStatus();
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    country,
    brandingInfo: { clientId },
    isExternalClient
  } = dataModel;
  const [configCountry, setConfigCountry] = useState(
    country || localeModel.country
  );

  const [config, setConfig] = useState<PartnerConfig>({
    ...defaultConfigs,
    // always show intercom for mt client and external client based on config or none
    enableIntercom: !isExternalClient,
    // disable as default for external, reason is if we failed to load a partner config,
    // it is better to no show remember me UI just in case some of them set to disabled
    disableRememberMe: isExternalClient
  } as PartnerConfig);

  useEffect(() => {
    const id = clientId || 'global';
    const url = `${dataModel.omoikaneUrl.replace(
      '{country}',
      configCountry
    )}/api/v1/client-app-info/${id}.json`;

    fetchWrapper(url, {
      method: 'GET',
      headers: { 'mt-app-name': 'my-account' }
    })
      .then((response) => {
        const { status } = response;

        if (status === 503) {
          setStatus({ maintenance: true });
          return;
        }

        if (status >= 500 && status < 600) {
          setStatus({ error: true });
          return;
        }

        return response.json().then(({ partnerConfigs }) => {
          // omoikane should return intercom true as default when it is global client id
          if (id === 'global') {
            partnerConfigs.enableIntercom = true;
          }

          setConfig({
            ...config,
            ...partnerConfigs,
            myAccount: parsedMyAccountConfig(partnerConfigs.myAccount),
            onboarding: parsedOnboardingConfig(partnerConfigs.onboarding)
          });
          setIsLoaded(true);
        });
      })
      .catch(() => {
        setStatus({ error: true });
        setIsLoaded(true);
      });
  }, [configCountry, clientId]);

  return (
    <PartnerConfigContext.Provider
      value={{ config, isLoaded, setConfigCountry }}
    >
      {children}
    </PartnerConfigContext.Provider>
  );
}
